import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { BookingFormProps, BookingData } from "../types";

interface FormData {
  projectName: string;
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  bookingTypes: string[];
  maxHeadcount: string;
}

interface FormErrors {
  projectName?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  email?: string;
  bookingTypes?: string;
  maxHeadcount?: string;
}

const BookingForm: React.FC<BookingFormProps> = ({
  selectedStudios,
  selectedSlots,
  onBookingData,
  onBack,
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState<FormData>({
    projectName: "",
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    bookingTypes: [],
    maxHeadcount: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState<boolean>(false);

  const bookingTypeOptions = [
    "Event",
    "Photoshoot",
    "Production (Film / Video)",
    "Other",
  ];

  const handleInputChange = (field: keyof FormData, value: string): void => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleBookingTypeChange = (type: string): void => {
    setFormData((prev) => ({
      ...prev,
      bookingTypes: [type], // Only allow single selection
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.projectName.trim()) {
      newErrors.projectName = "Project name is required";
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (formData.bookingTypes.length === 0) {
      newErrors.bookingTypes = "Please select at least one booking type";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Generate reCAPTCHA token
      let recaptchaToken = "";
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha("booking_form");
      }

      const bookingData: BookingData = {
        ...formData,
        maxHeadcount: formData.maxHeadcount
          ? parseInt(formData.maxHeadcount, 10)
          : undefined,
        studio: selectedSlots[0]?.studioId || selectedStudios[0]?.id || "",
        slots: selectedSlots,
        recaptchaToken,
      };

      onBookingData(bookingData);
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date: string): string => {
    return format(parseISO(date), "h:mm a");
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-primary-900 mb-4">
          Booking Details
        </h2>
        <p className="text-base sm:text-lg text-primary-600">
          Please provide your contact information and booking preferences.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
        {/* Selected Slots Summary */}
        <div className="bg-primary-50 rounded-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-primary-900 mb-2">
            Selected Time Range:
          </h3>
          <div className="text-sm sm:text-base text-primary-800">
            {format(parseISO(selectedSlots[0].start), "MMM d, yyyy")} -{" "}
            {formatTime(selectedSlots[0].start)} to{" "}
            {formatTime(selectedSlots[selectedSlots.length - 1].end)}
            <br />
            <span className="text-xs text-primary-600">
              (
              {Math.ceil(
                (new Date(
                  selectedSlots[selectedSlots.length - 1].end
                ).getTime() -
                  new Date(selectedSlots[0].start).getTime()) /
                  (60 * 60 * 1000)
              )}{" "}
              hours)
            </span>
          </div>
        </div>

        {/* Booking Form */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* Project Name */}
            <div>
              <label className="block text-sm font-medium text-primary-700 mb-2">
                Project/Event Name *
              </label>
              <input
                type="text"
                value={formData.projectName}
                onChange={(e) =>
                  handleInputChange("projectName", e.target.value)
                }
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.projectName
                    ? "border-primary-400"
                    : "border-primary-300"
                }`}
                placeholder="Enter project or event name"
              />
              {errors.projectName && (
                <p className="mt-1 text-sm text-primary-700">
                  {errors.projectName}
                </p>
              )}
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.firstName
                      ? "border-primary-400"
                      : "border-primary-300"
                  }`}
                  placeholder="First name"
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-primary-700">
                    {errors.firstName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.lastName
                      ? "border-primary-400"
                      : "border-primary-300"
                  }`}
                  placeholder="Last name"
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-primary-700">
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>

            {/* Company */}
            <div>
              <label className="block text-sm font-medium text-primary-700 mb-2">
                Company/Organization *
              </label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => handleInputChange("company", e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.company ? "border-primary-400" : "border-primary-300"
                }`}
                placeholder="Company or organization name"
              />
              {errors.company && (
                <p className="mt-1 text-sm text-primary-700">
                  {errors.company}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-primary-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.email ? "border-primary-400" : "border-primary-300"
                }`}
                placeholder="your.email@company.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-primary-700">{errors.email}</p>
              )}
            </div>

            {/* Max Headcount */}
            <div>
              <label className="block text-sm font-medium text-primary-700 mb-2">
                Max Headcount
              </label>
              <input
                type="number"
                min="1"
                value={formData.maxHeadcount}
                onChange={(e) =>
                  handleInputChange("maxHeadcount", e.target.value)
                }
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.maxHeadcount
                    ? "border-primary-400"
                    : "border-primary-300"
                }`}
                placeholder="Enter expected number of attendees"
              />
              {errors.maxHeadcount && (
                <p className="mt-1 text-sm text-primary-700">
                  {errors.maxHeadcount}
                </p>
              )}
            </div>

            {/* Booking Types */}
            <div>
              <label className="block text-sm font-medium text-primary-700 mb-2">
                Booking Type *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {bookingTypeOptions.map((type) => (
                  <label
                    key={type}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="bookingType"
                      checked={formData.bookingTypes.includes(type)}
                      onChange={() => handleBookingTypeChange(type)}
                      className="w-4 h-4 text-primary-600 border-primary-300 focus:ring-primary-500"
                    />
                    <span className="text-sm text-primary-700">{type}</span>
                  </label>
                ))}
              </div>
              {errors.bookingTypes && (
                <p className="mt-1 text-sm text-primary-700">
                  {errors.bookingTypes}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-4 sm:pt-6">
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 px-4 sm:px-6 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                  loading
                    ? "bg-primary-300 text-primary-500 cursor-not-allowed"
                    : "bg-primary-600 text-white hover:bg-primary-700"
                }`}
              >
                {loading ? "Processing..." : "Review & Confirm"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="flex justify-start sm:justify-start items-center mt-6 sm:mt-8">
        <button
          onClick={onBack}
          className="px-4 sm:px-6 py-3 border border-primary-300 rounded-lg text-primary-700 hover:bg-primary-50 transition-colors text-sm sm:text-base"
        >
          ← Back to Time Selection
        </button>
      </div>
    </div>
  );
};

export default BookingForm;
